import { useState, useEffect } from 'react';
import { DataTable, Button, useToast, Select } from '@/shared/components/ui';
import { Pencil, Trash2 } from 'lucide-react';
import { tipoUsuarioService } from '../api/tipoUsuarioService';
import { TipoUsuarioListaDto } from '@/core/sdk/modules/administracion/tipoUsuarioClient';
import { CreateTipoUsuarioModal } from '../ui/CreateTipoUsuarioModal';
import { EditTipoUsuarioModal } from '../ui/EditTipoUsuarioModal';
import { DeleteConfirmModal } from '../ui/DeleteConfirmModal';
import styles from './TiposUsuarioPage.module.css';

export const TiposUsuarioPage = () => {
    const [tiposUsuario, setTiposUsuario] = useState<TipoUsuarioListaDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActivo, setFilterActivo] = useState<boolean | 'all'>('all');

    const { showToast } = useToast();

    // Cargar tipos de usuario al montar
    useEffect(() => {
        loadTiposUsuario();
    }, []);

    const loadTiposUsuario = async () => {
        try {
            setLoading(true);
            const data = await tipoUsuarioService.getAll();
            setTiposUsuario(data);
        } catch (error: any) {
            showToast({
                type: 'error',
                message: error.message || 'Error al cargar tipos de usuario'
            });
        } finally {
            setLoading(false);
        }
    };

    // Filtrar datos
    const filteredData = tiposUsuario.filter(item => {
        const matchesSearch = item.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
        const matchesActivo = filterActivo === 'all' ? true : item.activo === filterActivo;
        return matchesSearch && matchesActivo;
    });

    // Columnas del DataTable
    const columns = [
        {
            key: 'nombre' as keyof TipoUsuarioListaDto,
            header: 'Nombre'
        },
        {
            key: 'descripcion' as keyof TipoUsuarioListaDto,
            header: 'Descripción',
            render: (_: any, row: TipoUsuarioListaDto) => row.descripcion || '-'
        },
        {
            key: 'activo' as keyof TipoUsuarioListaDto,
            header: 'Estado',
            render: (_: any, row: TipoUsuarioListaDto) => (
                <span className={row.activo ? styles.badgeActive : styles.badgeInactive}>
                    {row.activo ? 'Activo' : 'Inactivo'}
                </span>
            )
        },
        {
            key: 'cantidadUsuarios' as keyof TipoUsuarioListaDto,
            header: 'Usuarios',
            render: (_: any, row: TipoUsuarioListaDto) => row.cantidadUsuarios
        },
        {
            key: 'creadoEn' as keyof TipoUsuarioListaDto,
            header: 'Creado',
            render: (_: any, row: TipoUsuarioListaDto) => new Date(row.creadoEn).toLocaleDateString('es-ES')
        },
        {
            key: 'actions' as keyof TipoUsuarioListaDto,
            header: 'Acciones',
            render: (_: any, row: TipoUsuarioListaDto) => (
                <div className={styles.actions}>
                    <button
                        className={`${styles.iconButton} ${styles.editButton}`}
                        onClick={() => setEditingId(row.id)}
                        title="Editar"
                        aria-label={`Editar tipo de usuario ${row.nombre}`}
                    >
                        <Pencil size={16} strokeWidth={2.5} />
                    </button>
                    <button
                        className={`${styles.iconButton} ${styles.deleteButton}`}
                        onClick={() => setDeletingId(row.id)}
                        title="Eliminar"
                        aria-label={`Eliminar tipo de usuario ${row.nombre}`}
                    >
                        <Trash2 size={16} strokeWidth={2.5} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Tipos de Usuario</h1>
                <Button
                    variant="primary"
                    onClick={() => setShowCreateModal(true)}
                >
                    Nuevo Tipo de Usuario
                </Button>
            </div>

            <div className={styles.filters}>
                <input
                    type="text"
                    placeholder="Buscar por nombre o descripción..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                />

                <Select
                    options={[
                        { value: 'all', label: 'Todos los estados' },
                        { value: 'true', label: 'Activos' },
                        { value: 'false', label: 'Inactivos' }
                    ]}
                    value={filterActivo === 'all' ? 'all' : filterActivo ? 'true' : 'false'}
                    onChange={(val) => setFilterActivo(
                        val === 'all' ? 'all' : val === 'true'
                    )}
                    placeholder="Filtrar por estado"
                    className={styles.filterSelect}
                    noMargin
                />
            </div>

            <DataTable
                data={filteredData}
                columns={columns}
                loading={loading}
            />

            {/* Modal de Creación */}
            {showCreateModal && (
                <CreateTipoUsuarioModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        loadTiposUsuario();
                    }}
                />
            )}

            {/* Modal de Edición */}
            {editingId !== null && (
                <EditTipoUsuarioModal
                    isOpen={editingId !== null}
                    tipoUsuarioId={editingId}
                    onClose={() => setEditingId(null)}
                    onSuccess={() => {
                        setEditingId(null);
                        loadTiposUsuario();
                    }}
                />
            )}

            {/* Modal de Eliminación */}
            {deletingId !== null && (
                <DeleteConfirmModal
                    isOpen={deletingId !== null}
                    tipoUsuarioId={deletingId}
                    onClose={() => setDeletingId(null)}
                    onSuccess={() => {
                        setDeletingId(null);
                        loadTiposUsuario();
                    }}
                />
            )}
        </div>
    );
};
